import { BaseError } from "@/domains/errors";
import { api } from "@/modules/api";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { toastModule } from "@/modules/toast";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { action, makeObservable, observable, computed, runInAction } from "mobx";

export class SettingsAccountManagementPageStore extends AppSubStore {
  public isDeletingAccount = false;
  public hasRenewingSubscription = false;
  public isLoading = false;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);
    makeObservable(this, {
      isDeletingAccount: observable,
      hasRenewingSubscription: observable,
      currentAccountEmail: computed,
      refresh: action,
      handleDeleteAccount: action,
      isLoading: observable,
    });
  }

  refresh = async () => {
    if (this.isLoading) return;

    runInAction(() => {
      this.isLoading = true;
    });

    await Promise.all([this.store.platformInfo.fetchPlatformInfo()]);

    const subscription = this.store.subscriptions.personalSubscription;
    runInAction(() => {
      if (!subscription) {
        this.hasRenewingSubscription = false;
        this.isLoading = false;
        return;
      }

      if (subscription.isPaid && !subscription.isCanceled) {
        this.hasRenewingSubscription = true;
        this.isLoading = false;
        return;
      }

      this.hasRenewingSubscription = false;
      this.isLoading = false;
    });
  };

  get currentAccountEmail() {
    return this.store.account.myAccount.profileEmailAddress;
  }

  handleDeleteAccount = async () => {
    try {
      this.isDeletingAccount = true;
      const response = await api.delete("/v2/me/account");

      if (response.data) {
        this.store.navigation.goToLogOut();
      }

      if (response.error) {
        const err = new BaseError({
          message: "Failed to delete account.",
          info: { result: objectModule.safeAsJson(response) },
        });
        throw err;
      }
    } catch (unknownErr: unknown) {
      const error = unknownErr as BaseError;

      toastModule.triggerToast({
        content: "Failed to delete account. Please try again or contact support.",
      });

      logger.error({
        message: "[SettingsAccountManagementPageStore][handleDeleteAccount]: Error.",
        info: { error: objectModule.safeAsJson({ ...error }) },
      });
    } finally {
      this.isDeletingAccount = false;
    }
  };
}
